import type { ProductEntity } from '@/entities/assortment'
import type {
	AlertModel,
	PricingCampaignModel,
	ProductsQueryItemModel,
} from '@/shared/generated'

import React from 'react'
import { find, prop, propOr } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { usePriceManager } from '@/entities/assortment'
import { usePricingAlertsAnnotationsQuery } from '@/entities/price-alert'

import startOptImg from '@/shared/assets/img/svg/start_opt.svg'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { OptimizationStatus } from '@/shared/generated'
import { useOptimizationStatus } from '@/shared/hooks/data'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'

import Interpretation from './Interpretation'
import { SkuSidebarAlerts } from './SkuSidebarAlerts'

type EastsidePanelRuleInfoProps = {
	content: ProductEntity | ProductsQueryItemModel
	pricingCampaign?: PricingCampaignModel
}

export function PriceInfoTab({
	content,
	...props
}: EastsidePanelRuleInfoProps) {
	const { t } = useTranslation()

	const { data: alerts } = usePricingAlertsAnnotationsQuery({
		select: mapWithAnnotations(getProductsMessages(content as ProductEntity)),
	})
	const priceManager = usePriceManager({ content: content as ProductEntity })
	const optimizationGroupId =
		useOptimizationGroupId() || content.optimization_group_id
	const optimizationStatus = useOptimizationStatus(optimizationGroupId)

	return (
		<div className='mb-2 w-full space-y-2'>
			{priceManager.finalPrice.isChanged && (
				<Alert data-testid='info_0'>
					<AlertTitle>{t('price_info_manualy_changed_title')}</AlertTitle>
					<AlertDescription>
						{t('price_info_manualy_changed_desc')}
					</AlertDescription>
				</Alert>
			)}
			<SkuSidebarAlerts alerts={alerts} />
			{[OptimizationStatus.Failed, OptimizationStatus.Scheduled].includes(
				optimizationStatus!
			) ? (
				<Empty className='m-auto max-w-sm'>
					<EmptyHeader>
						<EmptyMedia>
							<img src={startOptImg} className='h-80' alt='No data' />
						</EmptyMedia>
						<EmptyDescription>{t('interpretation_empty')}</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : (
				<Interpretation content={content as ProductEntity} {...props} />
			)}
		</div>
	)
}
const mapWithAnnotations =
	(messages: string[]) =>
	(types: AlertModel[]): AlertModel[] =>
		messages.map((id) => find({ id }, types) as AlertModel).filter(Boolean)

const getProductsMessages = (content: ProductEntity) => [
	...(prop('fields.alerts', content) ||
		propOr([], 'custom_fields.alerts', content)),
]
